import { createApi } from "@reduxjs/toolkit/query/react";
import { 
  baseQuery,
  GET_PRODUCTS_WITH_TARIFFS,
  GET_PRODUCTS,
  CREATE_PRODUCT,
  UPDATE_PRODUCT,
  DELETE_PRODUCT,
  DELETE_PRODUCTS_BATCH,
  CREATE_PRODUCTS_BATCH,
  CALCULATE_DUTIES,
} from "@/graphql";

// Product interfaces based on API documentation
export interface Product {
  _id: string;
  sku: string;
  name: string;
  description?: string;
  htsCode: string;
  countryOfOrigin: string;
  metalPercentage?: number;
  metalBreakdown?: MetalBreakdownInput;
  tenantId?: string;
  clientId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdditionalTariff {
  columnId: string;
  columnName: string;
  rate: number;
}

// New interface for calculation breakdown
export interface CalculationBreakdown {
  columnId: string;
  columnName: string;
  isStackable: boolean;
  mutuallyExclusiveGroup?: string;
  applied: boolean;
}

// New types for enhanced duty breakdown
export enum LineType {
  METAL = "METAL",
  NONMETAL = "NONMETAL",
  FULL = "FULL",
  STEEL = "STEEL",
  ALUMINUM = "ALUMINUM",
  COPPER = "COPPER",
}

/** Derive per-metal input entries from applicable metal rule columns */
export function deriveMetalTypeEntries(
  metalRules: { name?: string }[]
): { key: string; label: string }[] {
  return metalRules.map((col) => {
    const nameLower = (col.name || "").toLowerCase();
    if (nameLower.includes("steel")) return { key: "steel", label: "Steel %" };
    if (nameLower.includes("aluminum") || nameLower.includes("aluminium"))
      return { key: "aluminum", label: "Aluminum %" };
    if (nameLower.includes("copper")) return { key: "copper", label: "Copper %" };
    // Unique key from column name to avoid collisions
    const safeKey = nameLower.replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    return { key: safeKey || "metal", label: `${col.name} %` };
  });
}

export interface ProductBreakdownLine {
  lineType: LineType;
  portion: number;
  applicableRules: AdditionalTariff[];
  totalRate: number;
  exclusionsApplied?: {
    listId: string;
    listName: string;
    columnId: string;
    columnName: string;
    authority?: string;
    matchedCode: string;
  }[];
  calculationBreakdown: CalculationBreakdown[];
}

export interface RiskAnalysis {
  riskLevel: string;
  riskReason: string;
  analyzedAt: string;
  analysisVersion: string;
}

export interface ProductWithTariffs extends Product {
  additionalTariffs: AdditionalTariff[];
  totalDutyRate?: number;
  breakdownLines?: ProductBreakdownLine[];
  riskAnalysis?: RiskAnalysis;
  secondaryDutyRateString?: string | null;
  potentialFTAPrograms?: string[];
  ftaApplicable?: boolean;
  ftaProgramCode?: string;
  ftaProgramName?: string;
}

export interface ProductsWithTariffsResponse {
  data: ProductWithTariffs[];
  meta: {
    countDocs: number;
    pageNo: number;
    pageSize: number;
  };
}

export interface MetalBreakdownInput {
  steel?: number;
  aluminum?: number;
  copper?: number;
}

export interface DutyCalculationInput {
  htsCode: string;
  countryOfOrigin: string;
  effectiveDate?: string;
  metalPercentage?: number;
  metalBreakdown?: MetalBreakdownInput;
}

export interface DutyCalculationPreview {
  additionalTariffs: AdditionalTariff[];
  totalDutyRate: number;
  breakdownLines: ProductBreakdownLine[];
  // optional metadata to enhance UI
  htsMatchedCode?: string;
  htsMatchLevel?: '10' | '8' | '6' | '4';
  htsDescription?: string | null;
  secondaryDutyRateString?: string | null;
  potentialFTAPrograms?: string[];
  ftaApplicable?: boolean;
  ftaProgramCode?: string;
  ftaProgramName?: string;
}

export interface ProductTariffQueryInput {
  effectiveDate: string;
  pageNo?: number;
  pageSize?: number;
  htsCode?: string;
  countryOfOrigin?: string;
  clientId?: string;
  tenantId?: string;
}

export interface CreateProductInput {
  sku: string;
  name: string;
  description?: string;
  htsCode: string;
  countryOfOrigin: string;
  metalPercentage?: number;
  metalBreakdown?: MetalBreakdownInput;
  clientId?: string;
  tenantId?: string;
}

export interface ProductsResponse {
  data: Product[];
  meta: {
    countDocs: number;
    pageNo: number;
    pageSize: number;
  };
}

// Batch product interfaces
export interface BatchProductInput {
  products: CreateProductInput[];
}

export interface BatchProductError {
  index: number;
  error: string;
  product: CreateProductInput;
}

export interface BatchDeleteResult {
  success: boolean;
  deletedCount: number;
}

export interface BatchProductResult {
  success: boolean;
  summary: {
    totalRequested: number;
    totalCreated: number;
    totalFailed: number;
  };
  createdProducts: Product[];
  errors: BatchProductError[];
}

export const productCatalogueApi = createApi({
  reducerPath: "api/productCatalogue",
  tagTypes: ["Product"],
  baseQuery,
  endpoints: (builder) => ({
    // Calculate duties for ad-hoc input
    calculateDuties: builder.query<DutyCalculationPreview, DutyCalculationInput>({
      query: (input) => ({
        document: CALCULATE_DUTIES,
        variables: { input },
      }),
      transformResponse: (response: { calculateDuties: DutyCalculationPreview }) =>
        response.calculateDuties,
    }),

    // Get products with calculated tariffs
    getProductsWithTariffs: builder.query<ProductsWithTariffsResponse, ProductTariffQueryInput>({
      query: (query) => ({
        document: GET_PRODUCTS_WITH_TARIFFS,
        variables: { query },
      }),
      transformResponse: (response: { productsWithTariffs: ProductsWithTariffsResponse }) =>
        response.productsWithTariffs,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ _id }) => ({ type: "Product" as const, id: _id })),
              { type: "Product", id: "LIST" },
            ]
          : [{ type: "Product", id: "LIST" }],
      // Merge function to optimize updates during polling
      merge: (currentCache, newItems) => {
        if (!currentCache) return newItems;
        
        // Create a map of new products for efficient lookup
        const newProductsMap = new Map(
          newItems.data.map(product => [product._id, product])
        );
        
        // Only keep products that exist in the new data (this handles deletions)
        const updatedData = currentCache.data
          .filter(existingProduct => newProductsMap.has(existingProduct._id))
          .map(existingProduct => {
            const newProduct = newProductsMap.get(existingProduct._id)!;
            
            // If product has been updated, use the new version
            if (newProduct.updatedAt !== existingProduct.updatedAt ||
                newProduct.riskAnalysis?.riskLevel !== existingProduct.riskAnalysis?.riskLevel) {
              return newProduct;
            }
            
            // Otherwise, keep the existing product to maintain referential stability
            return existingProduct;
          });
        
        // Add any new products that weren't in the existing data
        const existingIds = new Set(currentCache.data.map(p => p._id));
        const newProducts = newItems.data.filter(p => !existingIds.has(p._id));
        
        return {
          ...newItems,
          data: [...newProducts, ...updatedData],
        };
      },
    }),

    // Get all products (without tariff calculations)
    getProducts: builder.query<ProductsResponse, { filter?: any }>({
      query: (variables) => ({
        document: GET_PRODUCTS,
        variables,
      }),
      transformResponse: (response: { products: ProductsResponse }) =>
        response.products,
      providesTags: ["Product"],
    }),

    // Create a new product
    createProduct: builder.mutation<Product, { product: CreateProductInput }>({
      query: ({ product }) => ({
        document: CREATE_PRODUCT,
        variables: { product },
      }),
      transformResponse: (response: { createProduct: { data: Product } }) =>
        response.createProduct.data,
      invalidatesTags: [{ type: "Product", id: "LIST" }],
    }),

    // Update a product
    updateProduct: builder.mutation<Product, { id: string; product: Partial<CreateProductInput> }>({
      query: ({ id, product }) => ({
        document: UPDATE_PRODUCT,
        variables: { id, product },
      }),
      transformResponse: (response: { updateProduct: { data: Product } }) =>
        response.updateProduct.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Product", id },
        { type: "Product", id: "LIST" },
      ],
    }),

    // Delete a product
    deleteProduct: builder.mutation<boolean, string>({
      query: (id) => ({
        document: DELETE_PRODUCT,
        variables: { id },
      }),
      transformResponse: (response: { deleteProduct: boolean }) =>
        response.deleteProduct,
      invalidatesTags: (_result, _error, id) => [
        { type: "Product", id },
        { type: "Product", id: "LIST" },
      ],
    }),

    // Delete multiple products in batch
    deleteProductsBatch: builder.mutation<BatchDeleteResult, { ids: string[]; clientId?: string }>({
      query: ({ ids, clientId }) => ({
        document: DELETE_PRODUCTS_BATCH,
        variables: { ids, clientId },
      }),
      transformResponse: (response: { deleteProductsBatch: BatchDeleteResult }) =>
        response.deleteProductsBatch,
      invalidatesTags: [{ type: "Product", id: "LIST" }],
    }),

    // Create multiple products in batch
    createProductsBatch: builder.mutation<BatchProductResult, BatchProductInput>({
      query: (input) => ({
        document: CREATE_PRODUCTS_BATCH,
        variables: { input },
      }),
      transformResponse: (response: { createProductsBatch: BatchProductResult }) =>
        response.createProductsBatch,
      invalidatesTags: [{ type: "Product", id: "LIST" }],
    }),
  }),
});

export const {
  useGetProductsWithTariffsQuery,
  useLazyGetProductsWithTariffsQuery,
  useGetProductsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useDeleteProductsBatchMutation,
  useCreateProductsBatchMutation,
  useCalculateDutiesQuery,
} = productCatalogueApi; 